// ============================================================
// OrderRow - one combatant's seat on the order strip: the initiative
// box, the name (or its rename box), the role and status marks, and the
// reroll and close buttons. No vitals: the strip has no room for them
// (plan design-gaps L2); the rails and the map carry the wounds.
//
// Split from OrderRail, which owns the drags, the keyboard walk and the
// tracker's chrome; a row only reports what happened to it. The E2E
// contract in OrderRail's header covers the ids and attributes here.
// ============================================================
import LinkedChevrons from './killTeam/LinkedChevrons';
import { inSquadMode } from '../engine/killTeam/marks';
import { memo, useCallback, useEffect, useState } from 'react';
import type { DragEvent, KeyboardEvent, MouseEvent, ReactElement } from 'react';
import clsx from '../utils/clsx';
import styles from '../styles/OrderRail.module.css';
import common from '../styles/common.module.css';
import type { RailRow } from '../engine/roster/orderRail';
import { liveryStyle } from '../utils/liveryStyle';
import { isObject } from '../engine/bodyShape';
import { healthColor, healthGauge, vitalsOf } from '../engine/healthRamp';
import { useWoundDisplay } from '../hooks/useWoundDisplay';

/** "1d10(7) + AB 4 = 11", or what a hand-typed number is instead. */
function breakdown(row: RailRow): string {
  if (!row.inOrder) return 'Not in the order';
  if (row.initiative == null) return 'No initiative rolled';
  if (row.initiativeManual) return `Initiative ${row.initiative}, set by hand`;
  if (row.initiativeRoll == null) return `Initiative ${row.initiative}`;
  const bonus = row.initiativeBonus ?? 0;
  const sign = bonus < 0 ? '−' : '+';
  return `1d10(${row.initiativeRoll}) ${sign} AB ${Math.abs(bonus)} = ${row.initiative}`;
}

const ACTOR = 'var(--actor)';
const ACCENT = 'var(--accent)';
const RED = 'var(--auspex-red)';
const DIM = 'var(--text-dim)';

type MarkKind = 'chevron' | 'reticle' | 'now' | 'done' | 'downed';

const SVG = { width: 12, height: 12, viewBox: '0 0 12 12', 'aria-hidden': true as const };

// Declarative: each mark's strokes, as SVG children and the stroke style.
const MARKS: Record<MarkKind, (colour: string) => ReactElement> = {
  chevron: (colour) => (
    <svg {...SVG} fill="none" stroke={colour} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 3.5 L6 8 L10 3.5" />
    </svg>
  ),
  reticle: (colour) => (
    <svg {...SVG} fill="none" stroke={colour} strokeWidth="1.8" strokeLinecap="round">
      <path d="M1.5 4V1.5H4M8 1.5h2.5V4M10.5 8v2.5H8M4 10.5H1.5V8" />
      <circle cx="6" cy="6" r="1.4" fill={colour} stroke="none" />
    </svg>
  ),
  now: (colour) => (
    <svg {...SVG} fill={colour}>
      <path d="M2.5 1.5 L10.5 6 L2.5 10.5 Z" />
    </svg>
  ),
  done: (colour) => (
    <svg {...SVG} fill="none" stroke={colour} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 6.5 L5 9.5 L10 3" />
    </svg>
  ),
  downed: (colour) => (
    <svg {...SVG} fill="none" stroke={colour} strokeWidth="1.8" strokeLinecap="round">
      <circle cx="6" cy="6" r="4.5" />
      <path d="M4 4l4 4M8 4l-4 4" />
    </svg>
  ),
};

/** The role marks and the status glyphs, as strokes rather than dingbats. */
function Mark({ kind, colour, title }: { kind: MarkKind; colour: string; title: string }) {
  return <span title={title}>{MARKS[kind](colour)}</span>;
}

interface RowProps {
  row: RailRow;
  index: number;
  selected: boolean;
  hovered: boolean;
  locked: boolean;
  manageLocked: boolean;
  draggable: boolean;
  renaming: boolean;
  onSelect: (id: string) => void;
  onKeyNav: (e: KeyboardEvent<HTMLDivElement>) => void;
  onRenameStart: (id: string) => void;
  onRenameCommit: (id: string, value: string) => void;
  onRenameCancel: () => void;
  onContext: (row: RailRow, e: MouseEvent) => void;
  onClose: (id: string) => void;
  onSetInitiative: (id: string, value: number | null) => void;
  onReroll: (id: string) => void;
  onDragStart: (row: RailRow, index: number) => void;
  onDragOver: (row: RailRow, index: number) => void;
  onDragEnd: () => void;
  onHover: (id: string | null) => void;
}

/** The typed initiative: null for an empty box, NaN for a typo (which the caller refuses). */
function readInitiative(draft: string): number | null {
  const trimmed = draft.trim();
  return trimmed === '' ? null : Number.parseInt(trimmed, 10);
}

/**
 * A DRAFT WHILE YOU TYPE, committed on blur or Enter - the committed
 * setter snapshots for undo, syncs and RE-SORTS the list, and doing that
 * per keystroke shuffled the row out from under the cursor mid-"12".
 */
function useInitiativeDraft(row: RailRow, onSetInitiative: RowProps['onSetInitiative']) {
  const [draft, setDraft] = useState<string | null>(null);
  const shown = draft ?? (row.initiative ?? '').toString();
  useEffect(() => {
    setDraft(null);
  }, [row.initiative]);
  const commit = useCallback(() => {
    if (draft === null || !row.participantId) return;
    const parsed = readInitiative(draft);
    setDraft(null);
    // parseInt('abc') is NaN, and `|| 0` used to turn a typo into a 0 that
    // sorted last. Refuse it and keep what was there.
    if (Number.isNaN(parsed) || parsed === row.initiative) return;
    onSetInitiative(row.participantId, parsed);
  }, [draft, row.participantId, row.initiative, onSetInitiative]);
  return { shown, setDraft, commit };
}

type Draft = ReturnType<typeof useInitiativeDraft>;

function InitiativeInput({ row, pid, locked, draft }: { row: RailRow; pid: string; locked: boolean; draft: Draft }) {
  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    e.stopPropagation();
    if (e.key === 'Enter') e.currentTarget.blur();
    if (e.key === 'Escape') {
      draft.setDraft(null);
      e.currentTarget.blur();
    }
  };
  return (
    <input
      data-testid={`turn-initiative-${pid}`}
      className={styles.initInput}
      type="text"
      inputMode="numeric"
      value={draft.shown}
      onChange={(e) => draft.setDraft(e.target.value)}
      onBlur={draft.commit}
      onKeyDown={onKeyDown}
      onClick={(e) => e.stopPropagation()}
      placeholder="#"
      disabled={locked}
      aria-label={`Initiative for ${row.name}`}
    />
  );
}

function RenameInput({
  row,
  onCommit,
  onCancel,
}: {
  row: RailRow;
  onCommit: RowProps['onRenameCommit'];
  onCancel: () => void;
}) {
  return (
    <input
      className={clsx(common.input, styles.renameInput)}
      // eslint-disable-next-line jsx-a11y/no-autofocus -- rename appears on the user's own double-click
      autoFocus
      defaultValue={row.name}
      onClick={(e) => e.stopPropagation()}
      onBlur={(e) => onCommit(row.combatantId!, e.target.value)}
      onKeyDown={(e) => {
        e.stopPropagation();
        if (e.key === 'Enter') onCommit(row.combatantId!, e.currentTarget.value);
        if (e.key === 'Escape') onCancel();
      }}
    />
  );
}

function NameCell({
  row,
  renaming,
  onRenameCommit,
  onRenameCancel,
}: Pick<RowProps, 'row' | 'renaming' | 'onRenameCommit' | 'onRenameCancel'>) {
  return (
    <div className={styles.nameCell}>
      {renaming ? (
        <RenameInput row={row} onCommit={onRenameCommit} onCancel={onRenameCancel} />
      ) : (
        <span className={styles.name}>{row.name}</span>
      )}
      {isObject(row.target) && <ObjectBar row={row} />}
      {row.isCustom && <span className={styles.customMarker}>★</span>}
      {row.initiativeManual && (
        <span className={styles.manualMarker} title="Set by hand">
          ✋
        </span>
      )}
    </div>
  );
}

/**
 * AN OBJECT'S ROW (2026-10-26, board 7): it sits under "Not in the order" for good, so where the initiative would be
 * it wears its diamond, and after its name a health bar - stepped by band when the table plays without numbers.
 */
function ObjectBar({ row }: { row: RailRow }) {
  const { showNumbers } = useWoundDisplay();
  const { shown, total } = vitalsOf(row.target!);
  const fill = healthGauge(shown, total, showNumbers);
  return (
    <span className={styles.objectBar} data-testid={`object-bar-${row.combatantId}`} data-fill={fill} aria-hidden>
      <i style={{ width: `${fill}%`, background: healthColor(shown, total) }} />
    </span>
  );
}

/** The object's mark in the initiative's place: a diamond in its livery. */
function ObjectGlyph() {
  return (
    <svg {...SVG} className={styles.objectGlyph} data-testid="object-glyph">
      <path d="M6 0.8 L10.6 6 L6 11.2 L1.4 6 Z" />
    </svg>
  );
}

/** Status first (acting, done, downed - a downed row shows only that - or surprised), then who I am and whom I target. */
function RowMarks({ row }: { row: RailRow }) {
  const up = !row.downed;
  return (
    <div className={styles.marks}>
      {row.status === 'active' && up && <Mark kind="now" colour={ACTOR} title="Acting now" />}
      {row.status === 'done' && up && <Mark kind="done" colour={DIM} title="Done this round" />}
      {row.downed && <Mark kind="downed" colour={DIM} title="Out of the fight" />}
      {row.surprised && up && <Mark kind="downed" colour={ACCENT} title="Surprised - sits out this round" />}
      {row.isActor && <Mark kind="chevron" colour={ACTOR} title="You are acting as this combatant" />}
      {row.isTarget && <Mark kind="reticle" colour={RED} title="Your target" />}
      {inSquadMode(row.target) && <LinkedChevrons livery={row.livery} />}
    </div>
  );
}

function RowActions({
  row,
  pid,
  closable,
  locked,
  onReroll,
  onClose,
}: {
  row: RailRow;
  pid: string | null | undefined;
  closable: boolean;
  locked: boolean;
  onReroll: RowProps['onReroll'];
  onClose: RowProps['onClose'];
}) {
  return (
    <div className={styles.rowActions}>
      {pid && (
        <button
          type="button"
          data-testid={`turn-reroll-${pid}`}
          className={styles.rerollBtn}
          onClick={(e) => {
            e.stopPropagation();
            onReroll(pid);
          }}
          disabled={locked}
          title={`Reroll initiative for ${row.name}`}
          aria-label={`Reroll initiative for ${row.name}`}
        >
          {'↻'}
        </button>
      )}
      {closable && (
        // Mouse-only by the user's choice (2026-09-27): no keyboard close on rail rows.
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
        <span
          className={styles.closeBtn}
          title={`Close ${row.name}`}
          onClick={(e) => {
            e.stopPropagation();
            onClose(row.combatantId!);
          }}
        >
          x
        </span>
      )}
    </div>
  );
}

/** The row's tooltip: name, how its initiative came about, and what a click does. */
function rowTitle(row: RailRow, targetable: boolean): string {
  return (
    `${row.name}${row.isCustom ? ' (custom)' : ''}\n${breakdown(row)}` +
    (row.downed ? '\nSkipped - out of the fight' : '') +
    (row.surprised ? '\nSurprised - sits out this round' : '') +
    (targetable ? '\nClick to target · right-click for the menu' : '\nRight-click to remove')
  );
}

function rowClassName(row: RailRow, hovered: boolean, selected: boolean): string {
  return clsx(
    styles.row,
    hovered && styles.rowHovered,
    selected && styles.rowTarget,
    row.status === 'active' && styles.rowActive,
    row.status === 'done' && styles.rowDone,
    row.downed && styles.rowDowned,
    row.isCustom && styles.rowCustom,
    row.hidden && styles.rowHidden,
    row.livery && styles.liveried,
  );
}

/**
 * The row's own pointer and drag handlers. Only a row with a combatant
 * can be targeted, hovered or renamed; a custom participant has none.
 */
function rowHandlers(p: RowProps) {
  const { row, index, draggable } = p;
  const id = row.combatantId;
  return {
    onDragStart: () => draggable && p.onDragStart(row, index),
    onDragOver: (e: DragEvent) => {
      if (!draggable) return;
      e.preventDefault();
      p.onDragOver(row, index);
    },
    onDragEnd: p.onDragEnd,
    onPointerEnter: () => !!id && p.onHover(id),
    onPointerLeave: () => !!id && p.onHover(null),
    onClick: () => !!id && p.onSelect(id),
    onKeyDown: p.onKeyNav,
    onDoubleClick: () => !!id && !p.manageLocked && p.onRenameStart(id),
    onContextMenu: (e: MouseEvent) => {
      e.preventDefault();
      p.onContext(row, e);
    },
  };
}

function OrderRow(props: RowProps) {
  const { row, selected, hovered, locked, manageLocked, draggable } = props;
  const draft = useInitiativeDraft(row, props.onSetInitiative);
  const pid = row.participantId;
  const targetable = !!row.combatantId;

  return (
    <div
      role="tab"
      aria-selected={selected}
      aria-label={row.name}
      tabIndex={selected ? 0 : -1}
      data-testid={pid ? `turn-chip-${pid}` : `rail-row-${row.combatantId}`}
      data-combatant-id={row.combatantId ?? undefined}
      data-status={row.status ?? undefined}
      data-downed={row.downed || undefined}
      data-surprised={row.surprised || undefined}
      data-hidden={row.hidden || undefined}
      data-object={isObject(row.target) || undefined}
      data-livery={row.livery ?? undefined}
      style={liveryStyle(row.livery)}
      data-hovered={hovered || undefined}
      className={rowClassName(row, hovered, selected)}
      draggable={draggable}
      {...rowHandlers(props)}
      title={rowTitle(row, targetable)}
    >
      <div className={styles.init}>
        {row.inOrder && pid ? <InitiativeInput row={row} pid={pid} locked={locked} draft={draft} /> : null}
        {isObject(row.target) && <ObjectGlyph />}
      </div>
      <NameCell {...props} />
      <RowMarks row={row} />
      <RowActions
        row={row}
        pid={pid}
        closable={targetable && !manageLocked}
        locked={locked}
        onReroll={props.onReroll}
        onClose={props.onClose}
      />
    </div>
  );
}

export default memo(OrderRow);
